import {useId, useRef, type ReactNode} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {PI_CARDINAL, PI_FILL} from '../pi/piPaths';
import {stillInWebKit} from './plushKit';
import {usePointerGaze} from './usePointerGaze';

/**
 * The PAI plush mascot: a cream, fuzzy bean in a cardinal knit beanie, with a
 * hand-drawn π stitched on its tummy. Beady eyes follow the pointer, it
 * blinks and breathes on its own, and it has a small set of expressive
 * {@link PlushState}s so it can show what is going on (for example a
 * `thinking` state behind a loading screen, `happy` on success).
 *
 * All geometry is authored in {@link VIEWBOX}; the thought dots and sleepy
 * z's draw slightly outside it, so the SVG renders with `overflow: visible`.
 */

/** SVG viewBox the plush is authored in. */
const VIEWBOX = '0 0 200 220';

/** Height ÷ width of {@link VIEWBOX}. */
export const PLUSH_ASPECT = 220 / 200;

/** The plush's expressions. */
export type PlushState = 'idle' | 'happy' | 'thinking' | 'sleepy' | 'surprised';

/** Every {@link PlushState}, in display order. */
export const PLUSH_STATES: readonly PlushState[] = [
  'idle',
  'happy',
  'thinking',
  'sleepy',
  'surprised',
];

const INK = '#2A1E1C';
const CHEEK = '#F4A6AE';
const FOOT = '#E4CCA6';
const ARM = '#EFDDBF';

/** Thought dots and z's — a mid tan that reads on light and dark pages. */
const BUBBLE = '#D9C3A0';

/** Eye centers in viewBox units. */
const EYES = [
  {cx: 76, cy: 104},
  {cx: 124, cy: 104},
] as const;

/** Largest pupil shift toward the pointer, in viewBox units. */
const GAZE_TRAVEL = 3.5;

/** Where `thinking` looks — up and to the side, as if working something out. */
const THINKING_GAZE = {dx: 3, dy: -4};

/** Eye line as a fraction of the rendered height, for the pointer gaze. */
const EYE_LINE_FRACTION = 104 / 220;

/** Scale that fits {@link PI_FILL}'s glyph onto the tummy patch. */
const TUMMY_PI_SCALE = 0.19;

/** The π glyph's center in {@link PI_FILL}'s own coordinates. */
const PI_GLYPH_CENTER = {x: 109, y: 94};

/** Where the tummy π is centered, in viewBox units. */
const TUMMY_CENTER = {x: 100, y: 161};

const breathe = keyframes`
  0%, 100% { transform: scale(1, 1); }
  50% { transform: scale(1.02, 0.975); }
`;

const hop = keyframes`
  0%, 55%, 100% { transform: translateY(0) scale(1, 1); }
  12% { transform: translateY(0) scale(1.06, 0.92); }
  30% { transform: translateY(-16px) scale(0.96, 1.05); }
  46% { transform: translateY(0) scale(1.05, 0.94); }
`;

const startle = keyframes`
  0% { transform: translateY(0) scale(1, 1); }
  30% { transform: translateY(-12px) scale(0.95, 1.06); }
  60% { transform: translateY(0) scale(1.04, 0.96); }
  100% { transform: translateY(0) scale(1, 1); }
`;

const tilt = keyframes`
  0%, 100% { transform: rotate(-3deg); }
  50% { transform: rotate(3deg); }
`;

const blink = keyframes`
  0%, 93%, 100% { transform: scaleY(1); }
  96% { transform: scaleY(0.1); }
`;

const sway = keyframes`
  0%, 100% { transform: rotate(-3deg); }
  50% { transform: rotate(3deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.25; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
`;

const drift = keyframes`
  0% { opacity: 0; transform: translate(0, 0) scale(0.6); }
  25% { opacity: 1; }
  100% { opacity: 0; transform: translate(14px, -34px) scale(1.1); }
`;

const Svg = styled.svg<{$size: number}>`
  display: block;
  width: ${({$size}) => $size}px;
  height: ${({$size}) => $size * PLUSH_ASPECT}px;
  overflow: visible;

  g,
  circle,
  text {
    transform-box: view-box;
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      animation: none !important;
    }
  }
`;

/** Outer motion layer: the state's whole-body move (hop, startle, tilt). */
const Motion = styled.g<{$state: PlushState}>`
  transform-origin: 100px 205px;
  ${({$state}) => {
    switch ($state) {
      case 'happy':
        return css`
          animation: ${hop} 1.4s ease-in-out infinite;
        `;
      case 'surprised':
        return css`
          animation: ${startle} 0.5s ease-out 1;
        `;
      case 'thinking':
        return css`
          animation: ${tilt} 3s ease-in-out infinite;
        `;
      default:
        return css``;
    }
  }}
`;

/** Inner layer: slow breathing, deeper and slower when sleepy. */
const Breath = styled.g<{$isSleepy: boolean}>`
  transform-origin: 100px 205px;
  animation: ${breathe} ${({$isSleepy}) => ($isSleepy ? 4 : 3)}s ease-in-out
    infinite;
  ${stillInWebKit}
`;

const Hat = styled.g`
  transform-origin: 100px 70px;
  animation: ${sway} 3.4s ease-in-out infinite;
  ${stillInWebKit}
`;

const Eyes = styled.g`
  transform-box: fill-box;
  transform-origin: center;
  animation: ${blink} 4.6s ease-in-out infinite;
  ${stillInWebKit}
`;

const Pupils = styled.g`
  transition: transform 0.18s ease-out;
`;

const ThoughtDot = styled.circle<{$delaySec: number}>`
  transform-box: fill-box;
  transform-origin: center;
  animation: ${pulse} 1.2s ease-in-out ${({$delaySec}) => $delaySec}s infinite;
`;

const Zee = styled.text<{$delaySec: number}>`
  font-family: 'Jost', system-ui, sans-serif;
  font-weight: 700;
  opacity: 0;
  animation: ${drift} 2.7s ease-out ${({$delaySec}) => $delaySec}s infinite;
`;

/** Props for {@link PaiPlush}. */
export interface PaiPlushProps {
  /** Rendered width in px (height derives from {@link PLUSH_ASPECT}). */
  size: number;
  /** Expression to show. Omitted defaults to `idle`. */
  state?: PlushState;
  /** Beanie color. Omitted defaults to the Stanford cardinal (`PI_CARDINAL`). */
  hatColor?: string;
}

/**
 * Renders the PAI plush mascot (see module comment). Decorative: the SVG is
 * `aria-hidden`, so a caller that conveys meaning with a state must say it
 * in text too.
 *
 * @param props - See {@link PaiPlushProps}.
 * @return The mascot SVG.
 * @throws Never.
 */
export function PaiPlush({
  size,
  state = 'idle',
  hatColor = PI_CARDINAL,
}: PaiPlushProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const pointerGaze = usePointerGaze({
    targetRef: svgRef,
    maxTravel: GAZE_TRAVEL,
    eyeLineFraction: EYE_LINE_FRACTION,
  });
  const gaze = state === 'thinking' ? THINKING_GAZE : pointerGaze;
  // useId output contains colons, which break `url(#...)` references.
  const reactId = useId();
  const idBase = `plush${reactId.replace(/[^a-zA-Z0-9]/g, '')}`;
  const furId = `${idBase}-fur`;
  const fuzzId = `${idBase}-fuzz`;
  const softId = `${idBase}-soft`;

  return (
    <Svg
      ref={svgRef}
      $size={size}
      viewBox={VIEWBOX}
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient id={furId} cx="42%" cy="32%" r="75%">
          <stop offset="0%" stopColor="#FFF9EE" />
          <stop offset="60%" stopColor="#F6E9D3" />
          <stop offset="100%" stopColor="#E6D0AE" />
        </radialGradient>
        {/* Roughens every edge it is applied to, so shapes read as plush fur. */}
        <filter id={fuzzId} x="-10%" y="-10%" width="120%" height="120%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.9"
            numOctaves={2}
            seed={3}
          />
          <feDisplacementMap in="SourceGraphic" scale={3.5} />
        </filter>
        <filter id={softId}>
          <feGaussianBlur stdDeviation={2.2} />
        </filter>
      </defs>

      <Motion $state={state}>
        <Breath $isSleepy={state === 'sleepy'}>
          <g filter={`url(#${fuzzId})`}>
            <ellipse cx={76} cy={196} rx={20} ry={11} fill={FOOT} />
            <ellipse cx={124} cy={196} rx={20} ry={11} fill={FOOT} />
            <ellipse
              cx={30}
              cy={136}
              rx={13}
              ry={20}
              fill={ARM}
              transform="rotate(20 30 136)"
            />
            <ellipse
              cx={170}
              cy={136}
              rx={13}
              ry={20}
              fill={ARM}
              transform="rotate(-20 170 136)"
            />
            <path
              d="M100 30 C150 30 176 72 176 122 C176 166 146 194 100 194 C54 194 24 166 24 122 C24 72 50 30 100 30 Z"
              fill={`url(#${furId})`}
            />
          </g>

          <Hat>
            <g filter={`url(#${fuzzId})`}>
              <circle cx={100} cy={14} r={13} fill={hatColor} />
              <path
                d="M40 66 C44 32 70 20 100 20 C130 20 156 32 160 66 Z"
                fill={hatColor}
              />
              <rect
                x={36}
                y={58}
                width={128}
                height={18}
                rx={9}
                fill={hatColor}
              />
            </g>
            {/* Shading and ribbing, drawn over any hatColor. */}
            <circle cx={100} cy={14} r={13} fill="#fff" opacity={0.18} />
            <rect
              x={36}
              y={58}
              width={128}
              height={18}
              rx={9}
              fill="#fff"
              opacity={0.1}
            />
            <path
              d="M52 62 v10 M64 62 v10 M76 62 v10 M88 62 v10 M100 62 v10 M112 62 v10 M124 62 v10 M136 62 v10 M148 62 v10"
              stroke="#000"
              strokeOpacity={0.25}
              strokeWidth={2}
              strokeLinecap="round"
            />
          </Hat>

          <ellipse
            cx={100}
            cy={160}
            rx={30}
            ry={22}
            fill="#FFFBF4"
            filter={`url(#${softId})`}
          />
          <path
            d={PI_FILL}
            fill={PI_CARDINAL}
            transform={`translate(${TUMMY_CENTER.x - PI_GLYPH_CENTER.x * TUMMY_PI_SCALE} ${TUMMY_CENTER.y - PI_GLYPH_CENTER.y * TUMMY_PI_SCALE}) scale(${TUMMY_PI_SCALE})`}
          />

          {EYES.map(({cx}) => (
            <ellipse
              key={cx}
              cx={cx < 100 ? 62 : 138}
              cy={120}
              rx={12}
              ry={7}
              fill={CHEEK}
              opacity={state === 'happy' ? 0.95 : 0.75}
              filter={`url(#${softId})`}
            />
          ))}

          <Face state={state} gaze={gaze} />
        </Breath>
      </Motion>

      {state === 'thinking' &&
        [
          {cx: 160, cy: 40, r: 4},
          {cx: 172, cy: 26, r: 5.5},
          {cx: 186, cy: 10, r: 7},
        ].map(({cx, cy, r}, index) => (
          <ThoughtDot
            key={cx}
            cx={cx}
            cy={cy}
            r={r}
            fill={BUBBLE}
            $delaySec={index * 0.25}
          />
        ))}

      {state === 'sleepy' &&
        [
          {x: 150, y: 40, fontSize: 18},
          {x: 158, y: 36, fontSize: 24},
        ].map(({x, y, fontSize}, index) => (
          <Zee
            key={x}
            x={x}
            y={y}
            fontSize={fontSize}
            fill={BUBBLE}
            $delaySec={index * 1.35}
          >
            z
          </Zee>
        ))}
    </Svg>
  );
}

/**
 * The eyes and mouth for a {@link PlushState}.
 *
 * @param props.state - Expression to draw.
 * @param props.gaze - Pupil offset, used by the open-eyed states.
 * @return The face group.
 * @throws Never.
 */
function Face({
  state,
  gaze,
}: {
  state: PlushState;
  gaze: {dx: number; dy: number};
}): ReactNode {
  const stroke = {
    stroke: INK,
    strokeWidth: 3.6,
    strokeLinecap: 'round' as const,
    fill: 'none',
  };

  if (state === 'happy') {
    return (
      <g>
        {EYES.map(({cx, cy}) => (
          <path
            key={cx}
            d={`M ${cx - 8} ${cy + 3} Q ${cx} ${cy - 8} ${cx + 8} ${cy + 3}`}
            {...stroke}
          />
        ))}
        <path
          d="M89 115 Q100 116 111 115 Q109 130 100 130 Q91 130 89 115 Z"
          fill={INK}
        />
        <ellipse cx={100} cy={126} rx={5.5} ry={3} fill="#E97C8A" />
      </g>
    );
  }

  if (state === 'sleepy') {
    return (
      <g>
        {EYES.map(({cx, cy}) => (
          <path
            key={cx}
            d={`M ${cx - 8} ${cy} Q ${cx} ${cy + 7} ${cx + 8} ${cy}`}
            {...stroke}
          />
        ))}
        <ellipse cx={100} cy={121} rx={3.5} ry={4} fill={INK} />
      </g>
    );
  }

  const isSurprised = state === 'surprised';
  const eyeRx = isSurprised ? 8 : 6.5;
  const eyeRy = isSurprised ? 10 : 8;

  return (
    <g>
      <Eyes>
        <Pupils style={{transform: `translate(${gaze.dx}px, ${gaze.dy}px)`}}>
          {EYES.map(({cx, cy}) => (
            <g key={cx}>
              <ellipse cx={cx} cy={cy} rx={eyeRx} ry={eyeRy} fill={INK} />
              <circle cx={cx + 2.2} cy={cy - 3.4} r={2.2} fill="#fff" />
            </g>
          ))}
        </Pupils>
      </Eyes>
      {isSurprised && <ellipse cx={100} cy={122} rx={5.5} ry={7} fill={INK} />}
      {state === 'thinking' && (
        <path d="M94 119 Q100 117 106 120" {...stroke} strokeWidth={3.2} />
      )}
      {state === 'idle' && (
        <path d="M91 116 Q100 125 109 116" {...stroke} strokeWidth={3.2} />
      )}
    </g>
  );
}
